import * as React from 'react'
import { Button } from 'reactstrap'
import { useDrag, useDrop } from 'react-dnd'

interface EditablePromptTemplate {
  id: number
  sequence: number
  prompt: string
  options: string
  changedFields: string[]
  invalidFields: string[]
  saveState?: 'idle' | 'saving' | 'saved' | 'error'
  saveError?: string
}

interface DraggableTemplateItemProps {
  template: EditablePromptTemplate
  index: number
  isSelected: boolean
  onSelect: (template: EditablePromptTemplate) => void
  onEdit: (template: EditablePromptTemplate) => void
  onSave: (template: EditablePromptTemplate) => void
  onDelete: (template: EditablePromptTemplate) => void
  onMove: (fromIndex: number, toIndex: number) => void
  isDragging?: boolean
}

const TEMPLATE_ITEM_TYPE = 'template-item'

export const DraggableTemplateItem: React.FC<DraggableTemplateItemProps> = ({
  template,
  index,
  isSelected,
  onSelect,
  onEdit,
  onSave,
  onDelete,
  onMove,
}) => {
  const [{ isDragging }, drag] = useDrag({
    item: { type: TEMPLATE_ITEM_TYPE, index, template },
    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
    }),
  })

  const [{ isOver, canDrop }, drop] = useDrop({
    accept: TEMPLATE_ITEM_TYPE,
    hover: (item: any, monitor) => {
      if (!ref.current) {
        return
      }

      const dragIndex = item.index
      const hoverIndex = index

      // Don't replace items with themselves
      if (dragIndex === hoverIndex) {
        return
      }

      // Determine rectangle on screen
      const hoverBoundingRect = ref.current.getBoundingClientRect()

      // Get vertical middle
      const hoverMiddleY = (hoverBoundingRect.bottom - hoverBoundingRect.top) / 2

      // Determine mouse position
      const clientOffset = monitor.getClientOffset()
      if (!clientOffset) {
        return
      }

      // Get pixels to the top
      const hoverClientY = clientOffset.y - hoverBoundingRect.top

      // Only perform the move when the mouse has crossed half of the items height
      // When dragging downwards, only move when the cursor is below 50%
      // When dragging upwards, only move when the cursor is above 50%

      // Dragging downwards
      if (dragIndex < hoverIndex && hoverClientY < hoverMiddleY) {
        return
      }

      // Dragging upwards
      if (dragIndex > hoverIndex && hoverClientY > hoverMiddleY) {
        return
      }

      // Time to actually perform the action
      onMove(dragIndex, hoverIndex)

      // Note: we're mutating the monitor item here!
      // Generally it's better to avoid mutations,
      // but it's good here for the sake of performance
      // to avoid expensive index searches.
      item.index = hoverIndex
    },
    collect: (monitor) => ({
      isOver: monitor.isOver(),
      canDrop: monitor.canDrop(),
    }),
  })

  const ref = React.useRef<HTMLDivElement>(null)
  drag(drop(ref))

  const hasChanges = template.changedFields.length > 0
  const hasErrors = template.invalidFields.length > 0
  const isSaving = template.saveState === 'saving'
  const isSaved = template.saveState === 'saved'
  const hasError = template.saveState === 'error'

  const getStatusIcon = () => {
    if (isSaving) return <i className="fa fa-spinner fa-spin text-warning"></i>
    if (hasError) return <i className="fa fa-exclamation-triangle text-danger"></i>
    if (isSaved) return <i className="fa fa-check-circle text-success"></i>
    if (hasErrors) return <i className="fa fa-exclamation-circle text-danger"></i>
    if (hasChanges) return <i className="fa fa-circle text-warning"></i>
    return null
  }

  const truncateText = (text: string, maxLength: number = 50): string => {
    if (!text || text.length <= maxLength) return text || ''
    return text.substring(0, maxLength) + '...'
  }

  return (
    <div
      ref={ref}
      className={`draggable-template-item ${isDragging ? 'dragging' : ''} ${isSelected ? 'selected' : ''} ${isOver && canDrop ? 'drop-target' : ''}`}
      style={{ 
        opacity: isDragging ? 0.5 : 1,
        cursor: isDragging ? 'grabbing' : 'grab'
      }}
    >
      <div className={`template-row ${isSelected ? 'selected' : ''}`}>
        <div className="d-flex align-items-center p-3">
          {/* Drag Handle */}
          <div className="drag-handle me-3 text-muted d-flex align-items-center justify-content-center" style={{ cursor: 'grab', width: '32px', height: '32px' }}>
            <i className="fa fa-bars"></i>
          </div>

          {/* Sequence */}
          <div className="template-sequence me-3">
            <span className="badge bg-secondary">#{template.sequence}</span>
          </div>

          {/* Status Icon */}
          <div className="template-status me-3">
            {getStatusIcon()}
          </div>

          {/* Template Info - clickable area */}
          <div 
            className="template-info flex-grow-1 me-3" 
            onClick={() => onSelect(template)} 
            style={{ cursor: 'pointer' }}
          >
            {/* Prompt Preview */}
            <div className="template-preview">
              <span className="text-dark">
                {truncateText(template.prompt, 120)}
              </span>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="template-actions d-flex gap-2">
            <Button
              color="secondary"
              size="sm"
              className="template-action-btn"
              onClick={(e) => {
                e.stopPropagation()
                onEdit(template)
              }}
              title="Edit template"
            >
              <i className="fa fa-edit"></i>
            </Button>
            
            {hasChanges && (
              <Button
                color="success"
                size="sm"
                className="template-action-btn"
                onClick={(e) => {
                  e.stopPropagation()
                  onSave(template)
                }}
                disabled={hasErrors || isSaving}
                title={hasErrors ? 'Fix errors before saving' : 'Save changes'}
              >
                <i className={isSaving ? 'fa fa-spinner fa-spin' : 'fa fa-save'}></i>
              </Button>
            )}

            <Button
              color="danger"
              size="sm"
              className="template-action-btn"
              onClick={(e) => {
                e.stopPropagation()
                onDelete(template)
              }}
              title="Delete template"
            >
              <i className="fa fa-trash"></i>
            </Button>
          </div>
        </div>

        {/* Error Message */}
        {hasError && template.saveError && (
          <div className="alert alert-danger alert-sm mx-3 mb-2">
            <small>{template.saveError}</small>
          </div>
        )}
      </div>
    </div>
  )
}